import React, { useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, MapPin, FileText, Sparkles, CheckCircle, Clock, User, ChevronRight, TrendingUp, Wallet, Calendar, Star, KeyRound, Heart, AlertTriangle, Ban, Check } from 'lucide-react';
import { MobileLayout } from '../../components/MobileLayout';
import { Tabs } from '../../components/ui/Tabs';
import { useScreenInit } from '../../useScreenInit.js';
import { getApartmentById, getApartmentSpecs, TYPE_STYLE, scoreColor } from './appartamentiData';
type DetailTab = 'overview' | 'specifiche' | 'interventi' | 'contratto';
function StatCard({
  icon,
  label,
  value,
  sub,
  accent = 'navy'
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  sub?: string;
  accent?: 'navy' | 'coral' | 'teal-deep' | 'emerald';
}) {
  const accentMap: Record<string, string> = {
    navy: 'bg-navy/10 text-navy',
    coral: 'bg-coral/10 text-coral',
    'teal-deep': 'bg-teal-deep/10 text-teal-deep',
    emerald: 'bg-emerald-50 text-emerald-600'
  };
  return <div className="bg-white border border-border rounded-xl p-4" data-id="element-2064">
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center mb-3 ${accentMap[accent]}`} data-id="element-2065">
        {icon}
      </div>
      <div className="text-2xl font-bold text-navy leading-tight" data-id="element-2066">{value}</div>
      <div className="text-xs text-muted-foreground mt-0.5" data-id="element-2067">{label}</div>
      {sub && <div className="text-[11px] text-gray-400 mt-1" data-id="element-2068">{sub}</div>}
    </div>;
}
export function CmAppartamentoDettaglioPage() {
  useScreenInit();
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const navigate = useNavigate();
  const apartment = getApartmentById(id);
  const [tab, setTab] = useState<DetailTab>('overview');
  if (!apartment) {
    return <MobileLayout data-id="element-2069">
        <div className="text-center py-20" data-id="element-2070">
          <p className="text-sm text-muted-foreground mb-4" data-id="element-2071">
            Appartamento non trovato.
          </p>
          <Link to="/cm/clienti/appartamenti" className="inline-flex items-center gap-1.5 text-sm font-medium text-coral hover:underline" data-id="element-2072">
            <ArrowLeft size={15} data-id="element-2073" />
            Torna agli appartamenti
          </Link>
        </div>
      </MobileLayout>;
  }
  const remaining = apartment.cleaningsTotal - apartment.cleaningsDone;
  const pct = apartment.cleaningsTotal > 0 ? Math.round(apartment.cleaningsDone / apartment.cleaningsTotal * 100) : 0;
  const completed = apartment.interventions.filter(i => i.status === 'Completata' && i.score !== null);
  const avgScore = completed.length > 0 ? Math.round(completed.reduce((s, i) => s + (i.score ?? 0), 0) / completed.length) : null;
  const totalEarnings = apartment.interventions.reduce((s, i) => s + i.earning, 0);
  return <MobileLayout data-id="element-2074">
      {/* Back */}
      <Link to="/cm/clienti/appartamenti" className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-navy transition-colors mb-4" data-id="element-2075">
        <ArrowLeft size={16} data-id="element-2076" />
        Appartamenti
      </Link>

      {/* Header card */}
      <div className="bg-white border border-border rounded-xl shadow-sm overflow-hidden mb-6" data-id="element-2077">
        <div className="flex flex-col sm:flex-row" data-id="element-2078">
          <img src={apartment.cover} alt={apartment.name} className="w-full sm:w-44 h-40 sm:h-auto object-cover flex-shrink-0 bg-gray-100" data-id="element-2079" />
          <div className="p-4 lg:p-5 flex-1 min-w-0" data-id="element-2080">
            <div className="flex items-start justify-between gap-3" data-id="element-2081">
              <div className="min-w-0" data-id="element-2082">
                <div className="flex items-center gap-2 flex-wrap mb-1" data-id="element-2083">
                  <h1 className="text-xl lg:text-2xl font-bold text-navy" data-id="element-2084">
                    {apartment.name}
                  </h1>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${TYPE_STYLE[apartment.type]}`} data-id="element-2085">
                    {apartment.type}
                  </span>
                </div>
                <div className="flex items-center gap-1.5 text-sm text-muted-foreground" data-id="element-2086">
                  <MapPin size={14} className="flex-shrink-0" data-id="element-2087" />
                  <span className="truncate" data-id="element-2088">
                    {apartment.address} · {apartment.zone}
                  </span>
                </div>
                <div className="flex items-center gap-1.5 text-xs text-gray-400 mt-1.5" data-id="element-2089">
                  <User size={13} className="flex-shrink-0" data-id="element-2090" />
                  Referente: {apartment.referente}
                </div>
              </div>
              {apartment.contract?.active ? <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200 flex-shrink-0" data-id="element-2091">
                  <CheckCircle size={13} data-id="element-2092" />
                  Contratto attivo
                </span> : <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-500 border border-gray-200 flex-shrink-0" data-id="element-2093">
                  Nessun contratto
                </span>}
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs className="mb-6" scrollable value={tab} onValueChange={id => setTab(id as DetailTab)} items={[{
      id: 'overview',
      label: 'Overview'
    }, {
      id: 'specifiche',
      label: 'Specifiche casa'
    }, {
      id: 'interventi',
      label: 'Interventi'
    }, {
      id: 'contratto',
      label: 'Contratto e Guadagni'
    }]} data-id="element-2094" />

      {/* OVERVIEW */}
      {tab === 'overview' && <div className="space-y-6" data-id="element-2095">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-id="element-2096">
            <StatCard icon={<CheckCircle size={18} data-id="element-2098" />} label="Pulizie eseguite" value={String(apartment.cleaningsDone)} accent="teal-deep" data-id="element-2097" />
            <StatCard icon={<Clock size={18} data-id="element-2100" />} label="Pulizie rimanenti" value={apartment.cleaningsTotal > 0 ? String(remaining) : '—'} accent="coral" data-id="element-2099" />
            <StatCard icon={<Star size={18} data-id="element-2102" />} label="Score medio" value={avgScore !== null ? String(avgScore) : '—'} sub={avgScore !== null ? 'su 100' : undefined} accent="emerald" data-id="element-2101" />
            <StatCard icon={<Wallet size={18} data-id="element-2104" />} label="Guadagno totale" value={`€${totalEarnings}`} accent="navy" data-id="element-2103" />
          </div>

          {/* Cleaning progress */}
          {apartment.cleaningsTotal > 0 && <div className="bg-white border border-border rounded-xl p-4 lg:p-5" data-id="element-2105">
              <div className="flex items-center justify-between mb-2" data-id="element-2106">
                <h2 className="text-sm font-semibold text-navy" data-id="element-2107">
                  Avanzamento contratto
                </h2>
                <span className="text-sm font-semibold text-navy" data-id="element-2108">
                  {apartment.cleaningsDone}/{apartment.cleaningsTotal}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2 mb-2" data-id="element-2109">
                <div className="bg-coral h-2 rounded-full transition-all duration-300" style={{
            width: `${pct}%`
          }} data-id="element-2110" />
              </div>
              <p className="text-xs text-muted-foreground" data-id="element-2111">
                {remaining} pulizie ancora disponibili nel pacchetto attivo.
              </p>
            </div>}

          {/* Mini score trend */}
          {completed.length > 0 && <div className="bg-white border border-border rounded-xl p-4 lg:p-5" data-id="element-2112">
              <div className="flex items-center gap-2 mb-4" data-id="element-2113">
                <TrendingUp size={16} className="text-teal-deep" data-id="element-2114" />
                <h2 className="text-sm font-semibold text-navy" data-id="element-2115">
                  Qualità nel tempo
                </h2>
              </div>
              <div className="flex items-end gap-2 h-28" data-id="element-2116">
                {[...completed].reverse().slice(-8).map(i => <div key={i.id} className="flex-1 flex flex-col items-center gap-1.5" data-id="element-2117">
                      <div className="w-full bg-gray-100 rounded-md flex items-end h-full" data-id="element-2118">
                        <div className="w-full bg-teal-deep rounded-md transition-all" style={{
                height: `${i.score ?? 0}%`
              }} title={`${i.score}`} data-id="element-2119" />
                      </div>
                      <span className="text-[10px] text-gray-400" data-id="element-2120">
                        {i.score}
                      </span>
                    </div>)}
              </div>
            </div>}
        </div>}

      {/* SPECIFICHE CASA (read-only, host-defined) */}
      {tab === 'specifiche' && (() => {
      const specs = getApartmentSpecs(apartment.id);
      return <div className="space-y-6" data-id="element-2121">
              <div className="flex items-start gap-2 bg-navy/5 border border-navy/10 rounded-xl p-3" data-id="element-2122">
                <Sparkles size={16} className="text-navy flex-shrink-0 mt-0.5" data-id="element-2123" />
                <p className="text-xs text-navy" data-id="element-2124">
                  Specifiche impostate dall'host. Condividile con il cleaner
                  assegnato: vanno sempre rispettate durante l'intervento.
                </p>
              </div>

              {/* Accesso */}
              <div className="bg-white border border-border rounded-xl p-4 lg:p-5" data-id="element-2125">
                <div className="flex items-center gap-2 mb-4" data-id="element-2126">
                  <KeyRound size={16} className="text-coral" data-id="element-2127" />
                  <h2 className="text-sm font-semibold text-navy" data-id="element-2128">Accesso</h2>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-2129">
                  <div className="rounded-lg border border-border p-3" data-id="element-2130">
                    <span className="block text-[10px] uppercase tracking-wider font-semibold text-muted-foreground" data-id="element-2131">
                      Modalità
                    </span>
                    <span className="block text-sm font-semibold text-navy mt-0.5" data-id="element-2132">
                      {specs.access.type}
                    </span>
                  </div>
                  {specs.access.code && <div className="rounded-lg border border-border p-3" data-id="element-2133">
                      <span className="block text-[10px] uppercase tracking-wider font-semibold text-muted-foreground" data-id="element-2134">
                        Codice
                      </span>
                      <span className="block text-sm font-semibold text-navy mt-0.5 font-mono tracking-wide" data-id="element-2135">
                        {specs.access.code}
                      </span>
                    </div>}
                </div>
                {specs.access.notes && <p className="mt-3 text-sm text-foreground bg-gray-50 border border-gray-100 rounded-lg p-3" data-id="element-2136">
                    {specs.access.notes}
                  </p>}
              </div>

              {/* Preferenze */}
              <div className="bg-white border border-border rounded-xl p-4 lg:p-5" data-id="element-2137">
                <div className="flex items-center gap-2 mb-4" data-id="element-2138">
                  <Heart size={16} className="text-coral" data-id="element-2139" />
                  <h2 className="text-sm font-semibold text-navy" data-id="element-2140">
                    Preferenze
                  </h2>
                </div>
                {specs.preferences.desiderata.length > 0 && <div className="mb-4" data-id="element-2141">
                    <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2" data-id="element-2142">
                      Desiderata host
                    </h3>
                    <ul className="space-y-2" data-id="element-2143">
                      {specs.preferences.desiderata.map((d, idx) => <li key={idx} className="flex items-start gap-2.5 text-sm text-foreground" data-id="element-2144">
                          <CheckCircle size={16} className="text-emerald-500 flex-shrink-0 mt-0.5" data-id="element-2145" />
                          {d}
                        </li>)}
                    </ul>
                  </div>}
                {specs.preferences.favProducts.length > 0 && <div data-id="element-2146">
                    <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2" data-id="element-2147">
                      Prodotti preferiti
                    </h3>
                    <div className="flex flex-wrap gap-2" data-id="element-2148">
                      {specs.preferences.favProducts.map((p, idx) => <span key={idx} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200" data-id="element-2149">
                          <Check size={12} data-id="element-2150" />
                          {p}
                        </span>)}
                    </div>
                  </div>}
              </div>

              {/* Punti di attenzione */}
              <div className="bg-white border border-border rounded-xl p-4 lg:p-5" data-id="element-2151">
                <div className="flex items-center gap-2 mb-4" data-id="element-2152">
                  <AlertTriangle size={16} className="text-amber-500" data-id="element-2153" />
                  <h2 className="text-sm font-semibold text-navy" data-id="element-2154">
                    Punti di attenzione
                  </h2>
                </div>
                {specs.attention.delicateMaterials.length > 0 && <div className="mb-4" data-id="element-2155">
                    <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2" data-id="element-2156">
                      Materiali delicati
                    </h3>
                    <div className="flex flex-wrap gap-2" data-id="element-2157">
                      {specs.attention.delicateMaterials.map((m, idx) => <span key={idx} className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200" data-id="element-2158">
                          {m}
                        </span>)}
                    </div>
                    {specs.attention.materialNote && <p className="mt-3 text-sm text-amber-800 bg-amber-50/60 border border-amber-100 rounded-lg p-3" data-id="element-2159">
                        {specs.attention.materialNote}
                      </p>}
                  </div>}
                {specs.attention.avoidProducts.length > 0 && <div data-id="element-2160">
                    <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2" data-id="element-2161">
                      Prodotti da evitare
                    </h3>
                    <div className="flex flex-wrap gap-2" data-id="element-2162">
                      {specs.attention.avoidProducts.map((p, idx) => <span key={idx} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-red-50 text-red-600 border border-red-200" data-id="element-2163">
                          <Ban size={12} data-id="element-2164" />
                          {p}
                        </span>)}
                    </div>
                  </div>}
              </div>
            </div>;
    })()}

      {/* INTERVENTI */}
      {tab === 'interventi' && <div data-id="element-2165">
          {apartment.interventions.length > 0 ? <div className="bg-white rounded-xl border border-border shadow-sm overflow-hidden" data-id="element-2166">
              <div className="overflow-x-auto" data-id="element-2167">
                <table className="w-full text-left border-collapse min-w-[680px]" data-id="element-2168">
                  <thead data-id="element-2169">
                    <tr className="bg-gray-50 border-b border-border" data-id="element-2170">
                      <th className="px-4 lg:px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-2171">
                        Data
                      </th>
                      <th className="px-4 lg:px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-2172">
                        Tipo
                      </th>
                      <th className="px-4 lg:px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-2173">
                        Eseguita da
                      </th>
                      <th className="px-4 lg:px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-2174">
                        Score
                      </th>
                      <th className="px-4 lg:px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider text-right" data-id="element-2175">
                        Dettaglio
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border" data-id="element-2176">
                    {apartment.interventions.map(i => <tr key={i.id} onClick={() => navigate(`/cm/clienti/appartamenti/${apartment.id}/interventi/${i.id}`)} className="hover:bg-gray-50/50 transition-colors cursor-pointer" data-id="element-2177">
                        <td className="px-4 lg:px-6 py-4" data-id="element-2178">
                          <div className="flex items-center gap-2 text-sm text-navy font-medium whitespace-nowrap" data-id="element-2179">
                            <Calendar size={14} className="text-gray-400" data-id="element-2180" />
                            {i.date}
                          </div>
                        </td>
                        <td className="px-4 lg:px-6 py-4" data-id="element-2181">
                          <span className="text-sm text-gray-700 whitespace-nowrap" data-id="element-2182">
                            {i.type}
                          </span>
                        </td>
                        <td className="px-4 lg:px-6 py-4" data-id="element-2183">
                          <div className="flex items-center gap-2 text-sm text-gray-700 whitespace-nowrap" data-id="element-2184">
                            <span className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center text-[10px] font-bold text-navy" data-id="element-2185">
                              {i.cleaner.split(' ').map(p => p[0]).join('')}
                            </span>
                            {i.cleaner}
                          </div>
                        </td>
                        <td className="px-4 lg:px-6 py-4" data-id="element-2186">
                          {i.score !== null ? <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold border ${scoreColor(i.score)}`} data-id="element-2187">
                              <Sparkles size={12} data-id="element-2188" />
                              {i.score}
                            </span> : <span className="text-xs text-gray-400" data-id="element-2189">—</span>}
                        </td>
                        <td className="px-4 lg:px-6 py-4 text-right" data-id="element-2190">
                          <span className="inline-flex items-center justify-end text-coral" data-id="element-2191">
                            <ChevronRight size={18} data-id="element-2192" />
                          </span>
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>
            </div> : <div className="bg-white border border-border rounded-xl p-10 text-center" data-id="element-2193">
              <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mx-auto mb-3" data-id="element-2194">
                <FileText size={20} className="text-muted-foreground" data-id="element-2195" />
              </div>
              <p className="text-sm font-medium text-foreground" data-id="element-2196">
                Nessun intervento eseguito
              </p>
              <p className="text-xs text-muted-foreground mt-1" data-id="element-2197">
                Gli interventi compariranno qui una volta completati.
              </p>
            </div>}
        </div>}

      {/* CONTRATTO E GUADAGNI */}
      {tab === 'contratto' && <div className="space-y-6" data-id="element-2198">
          {/* Contract */}
          <div className="bg-white border border-border rounded-xl p-4 lg:p-5" data-id="element-2199">
            <div className="flex items-center gap-2 mb-4" data-id="element-2200">
              <FileText size={16} className="text-coral" data-id="element-2201" />
              <h2 className="text-sm font-semibold text-navy" data-id="element-2202">
                Contratto attivo
              </h2>
            </div>
            {apartment.contract?.active ? <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-id="element-2203">
                <div className="rounded-lg border border-border p-3" data-id="element-2204">
                  <span className="block text-[10px] uppercase tracking-wider font-semibold text-muted-foreground" data-id="element-2205">
                    Piano
                  </span>
                  <span className="block text-sm font-semibold text-navy mt-0.5" data-id="element-2206">
                    {apartment.contract.plan}
                  </span>
                </div>
                <div className="rounded-lg border border-border p-3" data-id="element-2207">
                  <span className="block text-[10px] uppercase tracking-wider font-semibold text-muted-foreground" data-id="element-2208">
                    Scadenza
                  </span>
                  <span className="block text-sm font-semibold text-navy mt-0.5" data-id="element-2209">
                    {apartment.contract.expiry}
                  </span>
                </div>
                <div className="rounded-lg border border-border p-3" data-id="element-2210">
                  <span className="block text-[10px] uppercase tracking-wider font-semibold text-muted-foreground" data-id="element-2211">
                    Prezzo / pulizia
                  </span>
                  <span className="block text-sm font-semibold text-navy mt-0.5" data-id="element-2212">
                    €{apartment.contract.pricePerClean}
                  </span>
                </div>
              </div> : <p className="text-sm text-muted-foreground" data-id="element-2213">
                Nessun contratto attivo per questo appartamento.
              </p>}
          </div>

          {/* Earnings */}
          <div className="bg-white border border-border rounded-xl p-4 lg:p-5" data-id="element-2214">
            <div className="flex items-center gap-2 mb-4" data-id="element-2215">
              <Wallet size={16} className="text-teal-deep" data-id="element-2216" />
              <h2 className="text-sm font-semibold text-navy" data-id="element-2217">Guadagni</h2>
            </div>
            <div className="grid grid-cols-2 gap-3 mb-5" data-id="element-2218">
              <div className="rounded-lg bg-navy/5 p-4" data-id="element-2219">
                <div className="text-2xl font-bold text-navy" data-id="element-2220">
                  €{totalEarnings}
                </div>
                <div className="text-xs text-muted-foreground mt-0.5" data-id="element-2221">
                  Totale generato
                </div>
              </div>
              <div className="rounded-lg bg-coral/5 p-4" data-id="element-2222">
                <div className="text-2xl font-bold text-coral" data-id="element-2223">
                  €
                  {completed.length > 0 ? Math.round(totalEarnings / completed.length) : 0}
                </div>
                <div className="text-xs text-muted-foreground mt-0.5" data-id="element-2224">
                  Media a intervento
                </div>
              </div>
            </div>
            <div className="space-y-2" data-id="element-2225">
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1" data-id="element-2226">
                Dettaglio incassi
              </h3>
              {apartment.interventions.map(i => <div key={i.id} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0" data-id="element-2227">
                  <div className="flex items-center gap-3 min-w-0" data-id="element-2228">
                    <div className="w-8 h-8 rounded-full bg-teal-deep/10 flex items-center justify-center flex-shrink-0" data-id="element-2229">
                      <CheckCircle size={14} className="text-teal-deep" data-id="element-2230" />
                    </div>
                    <div className="min-w-0" data-id="element-2231">
                      <div className="text-sm font-medium text-navy truncate" data-id="element-2232">
                        {i.type}
                      </div>
                      <div className="text-xs text-gray-400" data-id="element-2233">
                        {i.date} · {i.cleaner}
                      </div>
                    </div>
                  </div>
                  <span className="text-sm font-semibold text-navy flex-shrink-0" data-id="element-2234">
                    €{i.earning}
                  </span>
                </div>)}
              {apartment.interventions.length === 0 && <p className="text-sm text-muted-foreground" data-id="element-2235">
                  Nessun incasso registrato.
                </p>}
            </div>
          </div>
        </div>}
    </MobileLayout>;
}